import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MessageCircle, Calendar, Footprints, ArrowLeft, Send, Sparkles, Check } from "lucide-react";
import { GlossLogomark } from "@/components/GlossLogomark";
import { useSettings } from "@/contexts/SettingsContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ClientPreview() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [messages, setMessages] = useState([
    { from: "salon", text: `Hi! Welcome to ${settings.salon_name}. How can we help you today? 🌷`, time: "10:02 AM" },
  ]);
  const [name, setName] = useState("");
  const [services, setServices] = useState([]);
  const [step, setStep] = useState("intro");
  const [draft, setDraft] = useState("");
  const SERVICE_OPTIONS = ["Haircut", "Blow-dry", "Hair Color", "Facial", "Manicure", "Pedicure", "Body Massage", "Keratin"];

  const send = (text) => {
    setMessages((m) => [...m, { from: "you", text, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]);
    setDraft("");
  };

  const reply = (text, delay = 600) => {
    setTimeout(() => setMessages((m) => [...m, { from: "salon", text, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]), delay);
  };

  const startBooking = () => {
    setStep("name");
    send("Hi! I'd like to book a slot.");
    reply("Wonderful 💛 Could you share your name?");
  };

  const submitName = () => {
    if (!name.trim()) return;
    send(`I'm ${name}`);
    reply(`Lovely to meet you, ${name.split(" ")[0]}! What services would you like today?`);
    setStep("services");
  };

  const toggleService = (s) => {
    setServices((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  };

  const submitServices = async () => {
    if (services.length === 0) return;
    send(services.join(", "));
    reply("Just one moment, let me find a perfect time…", 400);
    try {
      const res = await api.post("/appointments", {
        client_name: name,
        client_phone: "+91 99000 00000",
        gender: "female",
        services_requested: services,
        entry_method: "WhatsApp",
        is_emergency: false,
        branch_id: "branch-001",
      });
      const a = res?.data?.data;
      const wait = Math.floor(8 + Math.random() * 14);
      reply(
        `Your token: ${a?.token_number || "S-007"} · Stylist: Priya · Est. wait: ~${wait} min · Services: ${services.join(" + ")} ✨`,
        1100
      );
      toast.success("WhatsApp booking confirmed", { description: `Token ${a?.token_number} created` });
      setStep("done");
    } catch (e) {
      reply("Hmm — couldn't reach the desk just now. Please try again.", 800);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="fixed top-0 left-0 right-0 z-40 glass-primary" style={{ height: 68 }}>
        <div className="h-full max-w-[1400px] mx-auto px-6 flex items-center gap-4">
          <button onClick={() => navigate("/personas")} className="btn-3d btn-glass-3d btn-sm"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-2.5">
            <GlossLogomark size={32} animate={false} />
            <div>
              <div className="font-display font-bold text-[16px]">GLOSS</div>
              <div className="font-inter text-[11px] text-[var(--color-text-muted)] -mt-0.5">Client Preview · Demo</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10" style={{ paddingTop: 110, paddingBottom: 80 }}>
        {/* Left — reach us */}
        <div>
          <div className="overline mb-2">Reach us</div>
          <h2 className="font-display font-bold text-[32px] tracking-[-0.02em] mb-6">How would you like to book?</h2>
          <div className="space-y-3">
            <ReachCard icon={Phone}      tone="gold"  title="Call us"        sub={settings.salon_landline} onClick={() => toast.info("Demo: dialing salon")} />
            <ReachCard icon={MessageCircle} tone="gold" title="WhatsApp"     sub={settings.salon_whatsapp} onClick={startBooking} />
            <ReachCard icon={Calendar}   tone="glass" title="Book online"    sub="Pick a slot on our booking page" onClick={() => navigate("/book")} />
            <ReachCard icon={Footprints} tone="subtle" title="Walk in"       sub={`123 Lane 7, ${settings.branch_name}`} onClick={() => toast.info("Walk-in queue is open")} />
          </div>
          <div className="mt-8 glass-subtle rounded-2xl p-5 flex items-start gap-3">
            <Sparkles size={18} className="text-[var(--color-gold-dark)] mt-0.5" />
            <div className="font-inter text-[13.5px] text-[var(--color-text-secondary)] leading-relaxed">
              <span className="font-sora font-semibold">Tap WhatsApp</span> to walk through a 30-second booking — your token appears in the chat as it would in the real app.
            </div>
          </div>
        </div>

        {/* Right — iPhone mockup */}
        <div className="flex justify-center">
          <div
            className="relative rounded-[44px] shadow-2xl p-2.5 anim-slide-up-lg"
            style={{ width: 360, height: 720, background: "linear-gradient(180deg,#1A1218 0%, #0F0A0F 100%)", boxShadow: "0 40px 80px -20px rgba(28,25,23,0.45)" }}
          >
            <div className="absolute left-1/2 -translate-x-1/2 top-1.5 w-32 h-6 rounded-b-2xl" style={{ background: "#0A0608" }} />
            <div className="w-full h-full rounded-[36px] overflow-hidden flex flex-col" style={{ background: "linear-gradient(180deg,#E7DDD2 0%, #DFD3C6 100%)" }}>
              {/* WhatsApp header */}
              <div className="px-4 py-3 flex items-center gap-3" style={{ background: "#075E54" }}>
                <ArrowLeft size={18} className="text-white" />
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><GlossLogomark size={22} animate={false} /></div>
                <div className="leading-tight">
                  <div className="font-sora text-white text-[13px] font-semibold">{settings.salon_name}</div>
                  <div className="font-inter text-white/70 text-[10px]">online</div>
                </div>
              </div>

              {/* Chat */}
              <div className="flex-1 overflow-y-auto px-3 py-4 space-y-2.5">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.from === "you" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] px-3 py-2 rounded-2xl shadow-sm font-inter text-[13px] leading-relaxed ${m.from === "you" ? "rounded-tr-md text-[#1C1917]" : "rounded-tl-md text-[#1C1917] bg-white"}`}
                         style={m.from === "you" ? { background: "#DCF8C6" } : {}}>
                      {m.text}
                      <div className="text-[9px] text-[var(--color-text-faint)] mt-1 text-right">{m.time}</div>
                    </div>
                  </div>
                ))}
                {step === "services" && (
                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {SERVICE_OPTIONS.map((s) => (
                      <button key={s} onClick={() => toggleService(s)}
                              className={`px-2.5 py-1 rounded-full text-[11.5px] font-inter font-medium border transition ${services.includes(s) ? "bg-[var(--color-gold-mid)] border-[var(--color-gold-dark)] text-white" : "bg-white border-[var(--color-text-faint)]/40 text-[var(--color-text-secondary)]"}`}>
                        {services.includes(s) && <Check size={11} className="inline mr-1" />}{s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="p-2 flex items-center gap-2" style={{ background: "#E7DDD2" }}>
                {step === "name" ? (
                  <>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name…"
                           className="flex-1 h-10 px-3 rounded-full bg-white font-inter text-[13px] outline-none" />
                    <button onClick={submitName} className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#075E54", color: "white" }}><Send size={16} /></button>
                  </>
                ) : step === "services" ? (
                  <>
                    <div className="flex-1 h-10 px-3 rounded-full bg-white flex items-center font-inter text-[13px] text-[var(--color-text-muted)]">{services.length} services selected</div>
                    <button onClick={submitServices} className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#075E54", color: "white" }}><Send size={16} /></button>
                  </>
                ) : (
                  <>
                    <input value={draft} onChange={(e) => setDraft(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter" && draft) { send(draft); reply("Got it. Type 'book' to start.", 500); }}}
                           placeholder={step === "done" ? "Booking complete ✓" : "Type a message…"} disabled={step === "done"}
                           className="flex-1 h-10 px-3 rounded-full bg-white font-inter text-[13px] outline-none disabled:opacity-60" />
                    <button onClick={startBooking} className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#075E54", color: "white" }}><MessageCircle size={16} /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function ReachCard({ icon: Icon, tone, title, sub, onClick }) {
  const toneStyles = {
    gold:   { btn: "btn-gold-3d",   iconCol: "#FFF7E4" },
    glass:  { btn: "btn-glass-3d",  iconCol: "var(--color-mauve)" },
    subtle: { btn: "btn-glass-3d",  iconCol: "var(--color-text-muted)" },
  }[tone];
  return (
    <button onClick={onClick} className={`btn-3d ${toneStyles.btn} w-full justify-start px-5 h-[68px] rounded-2xl`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,255,255,0.30)" }}>
        <Icon size={18} style={{ color: toneStyles.iconCol }} />
      </div>
      <div className="text-left ml-1">
        <div className="font-sora font-semibold text-[15px]">{title}</div>
        <div className="font-inter text-[12.5px] opacity-85 -mt-0.5">{sub}</div>
      </div>
    </button>
  );
}
